<template>
  <section class="lg:max-w-[600px] lg:mx-auto lg:!px-0">
    <StatusBar v-if="!isDesktopFlow" />
    <section :class="{ 'px-4': !isDesktopFlow }">
      <h1 class="text-2xl md:text-4xl font-bold font-heading mb-8">Checkout</h1>

      <CheckoutContactForm
        v-if="checkoutStripeForm"
        ref="checkoutContactFrom"
        :mountStripeForm="checkoutStripeForm.mountStripeForm"
        :unmountStripeForm="checkoutStripeForm.unmountStripeForm"
      />
      <CheckoutStripeForm ref="checkoutStripeForm" class="my-6" />
      <p
        v-if="error.message"
        id="error-display"
        class="p-2 mt-4 rounded border border-red-400 bg-red-200 text-red-600"
      >
        {{ error.message }}
      </p>
      <div v-if="!isDesktopFlow" class="price-information">
        <CheckoutDetailTable class="mb-8" />
        <CheckoutPromoCode class="my-8" />
      </div>
      <template v-if="requiresPayment ? checkoutContactFrom?.contactInfoSaved : true">
        <CheckboxInput
          id="accepts-marketing"
          v-model="contact.accepts_marketing"
          :label="`Keep me informed of upcoming ${settingStore.venue_name} shows`"
          guest
        />
        <div v-if="checkout_confirmation_text" class="mt-7">
          <CheckboxInput
            id="accepts-checkout-confirmation"
            v-model="acceptsCheckoutConfirmation"
            label="I agree to the following policy"
            guest
          />
          <div
            class="text-sm text-fg-g-primary mt-2 whitespace-pre-line"
            id="checkout-confirmation-text"
          >
            {{ checkout_confirmation_text }}
          </div>
        </div>
      </template>
      <p v-show="checkout_text" class="text-sm text-fg-g-primary mt-7">
        {{ checkout_text }}
      </p>
    </section>
    <CheckoutBottomBar
      id="checkout-bottom-bar"
      @click:next="handleBottomBarClick"
      :isNextButtonDisabled="isBottomBarButtonDisabled"
      :nextButtonText="bottomBarButtonText"
    />
  </section>
</template>

<script setup lang="ts">
import type { PaymentIntentResult } from '@stripe/stripe-js'
import { storeToRefs } from 'pinia'
import { computed, ref, useTemplateRef } from 'vue'

import CheckboxInput from '@/common/components/molecules/CheckboxInput.vue'
import CheckoutBottomBar from '@/modules/guestUi/components/organisms/CheckoutBottomBar.vue'
import CheckoutContactForm from '@/modules/guestUi/components/organisms/CheckoutContactForm.vue'
import CheckoutDetailTable from '@/modules/guestUi/components/organisms/CheckoutDetailTable.vue'
import CheckoutPromoCode from '@/modules/guestUi/components/organisms/CheckoutPromoCode.vue'
import CheckoutStripeForm from '@/modules/guestUi/components/organisms/CheckoutStripeForm.vue'
import StatusBar from '@/modules/guestUi/components/organisms/StatusBar.vue'
import { useCheckoutStore } from '@/stores/checkout'
import { useSettingStore } from '@/stores/setting'
import { ErrorData, blankError, clearError, displayHandled } from '@/utils/errors'

const settingStore = useSettingStore()
const { checkout_confirmation_text, checkout_text } = storeToRefs(settingStore)

const checkoutStore = useCheckoutStore()
const { contact, isDesktopFlow, requiresPayment, client_secret, is_timer_expired } =
  storeToRefs(checkoutStore)

const acceptsCheckoutConfirmation = ref<boolean>(false)
const isSaving = ref<boolean>(false)
const isPaying = ref<boolean>(false)
const error = ref<ErrorData>(blankError())

const checkoutContactFrom =
  useTemplateRef<InstanceType<typeof CheckoutContactForm>>('checkoutContactFrom')

const checkoutStripeForm =
  useTemplateRef<InstanceType<typeof CheckoutStripeForm>>('checkoutStripeForm')

const isFormValid = computed<boolean>(() => {
  if (!checkoutContactFrom.value) return false
  return (
    (!checkout_confirmation_text.value || acceptsCheckoutConfirmation.value) &&
    checkoutContactFrom.value.isContactFormValid
  )
})

async function submitPayment() {
  if (is_timer_expired.value) return
  try {
    isPaying.value = true
    clearError(error.value)

    let stripeResponse: PaymentIntentResult | undefined
    if (checkoutStore.requiresPayment && checkoutStripeForm.value) {
      await checkoutStripeForm.value.elements?.submit()
      stripeResponse = await checkoutStripeForm.value.stripe?.confirmPayment({
        elements: checkoutStripeForm.value.elements!,
        clientSecret: client_secret.value!,
        redirect: 'if_required',
        confirmParams: { return_url: window.location.href },
      })
    }
    await checkoutStore.checkout(stripeResponse)
  } catch (e) {
    displayHandled(e, error.value)
  } finally {
    isPaying.value = false
  }
}
const isBottomBarButtonDisabled = computed<boolean>(() => {
  if (!requiresPayment.value) {
    return isSaving.value || !isFormValid.value
  }
  if (checkoutContactFrom.value?.contactInfoSaved) {
    return isPaying.value || !isFormValid.value || !checkoutStripeForm.value?.isStripeFormComplete
  }
  return true
})

const bottomBarButtonText = computed<string>(() => {
  if (checkoutContactFrom.value?.contactInfoSaved) {
    return isPaying.value ? 'Processing...' : 'Purchase'
  }
  if (requiresPayment.value) {
    return 'Purchase'
  } else {
    return isSaving.value ? 'Reserving...' : 'Reserve'
  }
})

async function handleBottomBarClick(): Promise<void> {
  if (isBottomBarButtonDisabled.value) return

  if (checkoutContactFrom.value?.contactInfoSaved) {
    await submitPayment()
    return
  }

  if (!requiresPayment.value) {
    isSaving.value = true
    clearError(error.value)
    try {
      await checkoutStore.setContactInfo()
      await checkoutStore.checkout(undefined)
    } catch (e) {
      displayHandled(e, error.value)
    } finally {
      isSaving.value = false
    }
  }
}
</script>
